import { useContext } from 'react'
import { useSelector } from 'react-redux'

import {
  useForm, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'

import defaultFormState, { requiredFields } from '@models/orderLeadTime'

import PageContext from '@contexts/pageContext'

import * as orderLeadTimeActions from '@redux/modules/orderLeadTime'

const getLeadTimeText = (orderLeadTime = {}) => {
  const { unit, value } = orderLeadTime
  let unitText

  if (unit === 'days'){
    unitText = value === 1 ? 'day' : unit
  }

  if (unit === 'weeks'){
    unitText = value === 1 ? 'week' : unit
  }

  return `${value} ${unitText}`
}

const createOrderLeadTime = (params) => {
  const { orderLeadTimeParams, dispatch, requestOptions } = params
  const { createOrderLeadTime: createFn } = orderLeadTimeActions

  return dispatch(createFn(orderLeadTimeParams, requestOptions))
}

const updateOrderLeadTime = (params) => {
  const {
    orderLeadTime, orderLeadTimeParams, dispatch, requestOptions,
  } = params
  const { updateOrderLeadTime: updateFn } = orderLeadTimeActions

  const updatedParams = {
    ...orderLeadTime,
    ...orderLeadTimeParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

const deleteOrderLeadTime = (params) => {
  const { orderLeadTime, dispatch, requestOptions } = params
  const { deleteOrderLeadTime: deleteFn } = orderLeadTimeActions

  return dispatch(deleteFn(orderLeadTime, requestOptions))
}

const createOrEditOrderLeadTime = params => new Promise((resolve, reject) => {
  const { orderLeadTime, showCreateOrEditOrderLeadTimeModal } = params

  if (showCreateOrEditOrderLeadTimeModal){
    const payload = {
      orderLeadTime,
    }

    showCreateOrEditOrderLeadTimeModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditOrderLeadTimeModal not defined in PageContext callbacks'))
})

export function useOrderLeadTimeForm(orderLeadTime = {}, options = {}){
  const { customRequiredFields = [] } = options

  const orderLeadTimeForm = useForm(
    defaultFormState,
    { entity: orderLeadTime, requiredFields: [...requiredFields, ...customRequiredFields], validateOn: 'change' },
    [orderLeadTime.id, orderLeadTime.cache_key],
  )

  return {
    ...orderLeadTimeForm,
  }
}

export function useRelations(orderLeadTime){
  const { orderable_id, orderable_type } = orderLeadTime || {}

  const serviceProducts = useSelector(reduxState => reduxState.entities.serviceProducts)

  const serviceProduct = orderable_type === 'ServiceProduct' ? serviceProducts[orderable_id] || {} : {}

  return {
    serviceProduct,
  }
}

function useOrderLeadTime(initEntity){
  const { entity: orderLeadTime } = useLatestEntity(initEntity, 'orderLeadTimes')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditOrderLeadTimeModal } = callbacks || {}

  const { serviceProduct } = useRelations(orderLeadTime)

  const { creating, deleting, updating } = useSelector(reduxState => reduxState.orderLeadTimes)

  const leadTimeText = getLeadTimeText(orderLeadTime)

  return {
    callbacks: {
      createOrEditOrderLeadTime: () => (
        createOrEditOrderLeadTime({ orderLeadTime, dispatch, showCreateOrEditOrderLeadTimeModal })
      ),
      createOrderLeadTime: (params, requestOptions) => (
        createOrderLeadTime({ orderLeadTimeParams: params, dispatch, requestOptions })
      ),
      deleteOrderLeadTime: requestOptions => deleteOrderLeadTime({ orderLeadTime, dispatch, requestOptions }),
      updateOrderLeadTime: (params, requestOptions) => updateOrderLeadTime({
        orderLeadTime, orderLeadTimeParams: params, dispatch, requestOptions,
      }),
    },
    creating,
    deleting,
    leadTimeText,
    orderLeadTime,
    serviceProduct,
    updating,
  }
}

export default useOrderLeadTime
